(() => {
  const rootSelector = '[data-clwb-club-design]';
  const listenerKey = 'clwbClubDesignJourneyListener';

  function initJourneys(scope = document) {
    const roots = [];

    if (scope.matches?.(rootSelector)) {
      roots.push(scope);
    }

    roots.push(...Array.from(scope.querySelectorAll?.(rootSelector) || []));
    roots.forEach(initJourney);
  }

  function initJourney(root) {
    if (root.dataset.clubDesignInitialised === 'true') {
      return;
    }

    root.dataset.clubDesignInitialised = 'true';

    const form = root.querySelector('.clwb-club-design__form');
    const app = root.querySelector('.clwb-club-design__app');
    const intro = root.querySelector('.clwb-club-design__intro');
    const success = root.querySelector('[data-club-design-success]');
    const shopifyError = root.querySelector('.clwb-club-design__shopify-error');
    const storageKey = root.dataset.storageKey || 'clwb_club_design_enquiry_v1';
    const totalSteps = Number(root.dataset.totalSteps || 8);
    const steps = Array.from(root.querySelectorAll('[data-step]'));
    const progress = root.querySelector('[data-club-design-progress]');
    const progressBar = root.querySelector('[data-club-design-progress-bar]');
    const stepText = root.querySelector('[data-club-design-step-text]');
    const errorSummary = root.querySelector('[data-error-summary]');
    const nextButton = root.querySelector('[data-next-step]');
    const prevButton = root.querySelector('[data-prev-step]');
    const submitButton = root.querySelector('[data-submit-journey]');
    const startAgainButton = root.querySelector('[data-start-again]');
    const startLink = root.querySelector('[data-club-design-start]');
    const siteExitLink = root.querySelector('[data-club-design-site-exit]');
    const journeyExitButton = root.querySelector('[data-club-design-journey-exit]');
    const exitDialog = root.querySelector('[data-club-design-exit-dialog]');
    let currentStep = 1;
    let started = false;
    let storageAvailable = false;
    let isSubmitting = false;
    let submitWatchdog = null;
    let pageIsUnloading = false;
    const submitButtonText = submitButton?.textContent || 'SEND MY ENQUIRY';

    try {
      const testKey = `${storageKey}_test`;
      window.sessionStorage.setItem(testKey, '1');
      window.sessionStorage.removeItem(testKey);
      storageAvailable = true;
    } catch (error) {
      storageAvailable = false;
    }

    if (!form || !app || !steps.length) {
      return;
    }

    if (success) {
      clearStorage();
      root.classList.add('clwb-club-design--submitted');
      intro.hidden = true;
      app.hidden = true;
      success.focus({ preventScroll: true });
      return;
    }

    restoreState();

    if (shopifyError) {
      started = true;
    }

    updateDerivedFields();
    updateStep(currentStep, { focus: false, save: false });
    setStarted(started, { focus: false, save: false });

    if (shopifyError) {
      shopifyError.focus({ preventScroll: true });
    }

    form.addEventListener('input', handleChange);
    form.addEventListener('change', handleChange);

    form.addEventListener('submit', (event) => {
      if (isSubmitting) {
        return;
      }

      if (!validateAllSteps()) {
        event.preventDefault();
        return;
      }

      updateDerivedFields();
      saveState();
      startSubmitting();
    });

    window.addEventListener('pagehide', markPageUnloading, { once: true });
    window.addEventListener('beforeunload', markPageUnloading, { once: true });

    nextButton?.addEventListener('click', () => {
      if (validateStep(currentStep)) {
        updateStep(Math.min(currentStep + 1, totalSteps), { focus: true });
      }
    });

    prevButton?.addEventListener('click', () => {
      updateStep(Math.max(currentStep - 1, 1), { focus: true });
    });

    root.querySelectorAll('[data-edit-step]').forEach((button) => {
      button.addEventListener('click', () => {
        updateStep(Number(button.dataset.editStep), { focus: true });
      });
    });

    startAgainButton?.addEventListener('click', () => {
      if (!window.confirm('Start again and clear the details entered in this browser session?')) {
        return;
      }

      resetJourney({ remainStarted: true });
    });

    startLink?.addEventListener('click', (event) => {
      event.preventDefault();
      setStarted(true, { focus: false });
      updateStep(currentStep, { focus: true });
    });

    siteExitLink?.addEventListener('click', (event) => {
      if (!hasEnteredData()) {
        return;
      }

      const shouldLeave = window.confirm(
        'Leave CLWB Sports? Your journey details will remain saved in this browser session.'
      );

      if (!shouldLeave) {
        event.preventDefault();
      }
    });

    journeyExitButton?.addEventListener('click', () => {
      if (!hasEnteredData()) {
        returnToIntroduction();
        return;
      }

      if (typeof exitDialog?.showModal === 'function') {
        exitDialog.showModal();
      }
    });

    root.querySelector('[data-exit-keep]')?.addEventListener('click', () => {
      closeExitDialog();
      returnToIntroduction();
    });

    root.querySelector('[data-exit-discard]')?.addEventListener('click', () => {
      closeExitDialog();
      resetJourney({ remainStarted: false });
    });

    root.querySelector('[data-exit-cancel]')?.addEventListener('click', () => {
      closeExitDialog();
      journeyExitButton?.focus();
    });

    exitDialog?.addEventListener('cancel', (event) => {
      event.preventDefault();
      closeExitDialog();
      journeyExitButton?.focus();
    });

    root.querySelectorAll('[data-toggle-panel]').forEach((control) => {
      control.addEventListener('change', () => {
        updateToggles();
        saveState();
      });
    });

    function handleChange(event) {
      const target = event.target;

      if (target.matches('[data-colour-input]')) {
        enforceColourLimit(target);
      }

      updateDerivedFields();
      saveState();
    }

    function setStarted(value, options = {}) {
      started = Boolean(value);
      root.classList.toggle('clwb-club-design--started', started);
      intro.hidden = started;
      app.hidden = !started;

      if (options.save !== false) {
        saveState();
      }

      if (!started) {
        root.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });

        if (options.focus !== false) {
          window.requestAnimationFrame(() => startLink?.focus({ preventScroll: true }));
        }
      }
    }

    function returnToIntroduction() {
      setStarted(false, { focus: true });
    }

    function resetJourney({ remainStarted }) {
      form.reset();
      currentStep = 1;
      started = Boolean(remainStarted);
      clearStorage();
      clearAllErrors();
      updateDerivedFields();
      updateStep(1, { focus: remainStarted, save: false });
      setStarted(remainStarted, { focus: !remainStarted, save: true });
    }

    function closeExitDialog() {
      if (exitDialog?.open) {
        exitDialog.close();
      }
    }

    function updateStep(step, options = {}) {
      currentStep = Math.min(Math.max(step, 1), totalSteps);

      steps.forEach((stepElement) => {
        const isActive = Number(stepElement.dataset.step) === currentStep;
        stepElement.hidden = !isActive;
        stepElement.classList.remove('clwb-club-design__step--entering');

        if (isActive && !prefersReducedMotion()) {
          window.requestAnimationFrame(() => stepElement.classList.add('clwb-club-design__step--entering'));
        }
      });

      if (stepText) {
        stepText.textContent = `STEP ${currentStep} OF ${totalSteps}`;
      }

      if (progress) {
        progress.setAttribute('aria-valuenow', String(currentStep));
      }

      if (progressBar) {
        progressBar.style.width = `${(currentStep / totalSteps) * 100}%`;
      }

      if (prevButton) {
        prevButton.hidden = currentStep === 1;
        prevButton.textContent =
          currentStep === totalSteps
            ? prevButton.dataset.reviewLabel || 'GO BACK'
            : prevButton.dataset.defaultLabel || 'Previous';
      }

      if (nextButton) {
        nextButton.hidden = currentStep === totalSteps;
      }

      if (submitButton) {
        submitButton.hidden = currentStep !== totalSteps;
      }

      if (currentStep === totalSteps) {
        updateReview();
      }

      if (!options.preserveSummary) {
        hideSummary();
      }

      if (options.save !== false) {
        saveState();
      }

      if (options.focus) {
        root.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
        const heading = getStep(currentStep)?.querySelector('legend');
        window.requestAnimationFrame(() => heading?.focus({ preventScroll: true }));
      }
    }

    function validateAllSteps() {
      for (let step = 1; step <= totalSteps - 1; step += 1) {
        if (!validateStep(step)) {
          updateStep(step, { focus: true, preserveSummary: true });
          return false;
        }
      }

      updateDerivedFields();
      return true;
    }

    function validateStep(stepNumber) {
      const step = getStep(stepNumber);

      if (!step) {
        return true;
      }

      clearStepErrors(step);
      let firstInvalid = null;

      step.querySelectorAll('input, select, textarea').forEach((control) => {
        if (control.type === 'hidden' || control.disabled || control.type === 'radio') {
          return;
        }

        const invalidMessage = getControlError(control);

        if (invalidMessage) {
          setControlError(control, invalidMessage);
          firstInvalid = firstInvalid || control;
        }
      });

      if (stepNumber === 2) {
        const colourError = validateColourGroup('home');
        if (colourError) {
          firstInvalid = firstInvalid || setGroupError('homeColors', colourError);
        }
      }

      if (stepNumber === 3) {
        const colourError = validateColourGroup('away');
        if (colourError) {
          firstInvalid = firstInvalid || setGroupError('awayColors', colourError);
        }
      }

      if (stepNumber === 4 && !getData().package) {
        firstInvalid = firstInvalid || setGroupError(
          'package',
          getPackageGroup()?.dataset.errorRequired || 'Choose one package starting point.'
        );
      }

      if (stepNumber === 6 && !getData().designStyle) {
        firstInvalid = firstInvalid || setGroupError(
          'designStyle',
          getDesignStyleGroup()?.dataset.errorRequired || 'Choose one design style.'
        );
      }

      if (firstInvalid) {
        showSummary('Please check the highlighted fields before continuing.');
        firstInvalid.focus({ preventScroll: false });
        return false;
      }

      hideSummary();
      return true;
    }

    function getControlError(control) {
      if (control.required) {
        if (control.type === 'checkbox' && !control.checked) {
          return control.dataset.errorRequired || 'Confirm this option to continue.';
        }

        if (control.type !== 'checkbox' && !control.value.trim()) {
          return control.dataset.errorRequired || 'Complete this field to continue.';
        }
      }

      if (control.type === 'email' && control.value.trim() && !control.validity.valid) {
        return control.dataset.errorEmail || 'Enter a valid email address.';
      }

      if (control.type === 'url' && control.value.trim() && !control.validity.valid) {
        return control.dataset.errorUrl || 'Enter a valid link starting with https:// or leave this blank.';
      }

      if (control.type === 'number' && control.value.trim() && !control.validity.valid) {
        return control.dataset.errorRange || 'Enter a valid number.';
      }

      return '';
    }

    function validateColourGroup(groupName) {
      const group = getColourGroup(groupName);

      if (!group) {
        return '';
      }

      if (groupName === 'away' && getFieldControl('awaySame')?.checked) {
        return '';
      }

      const selectedCount = getSelectedColours(groupName).length;
      const min = Number(group.dataset.min || 0);
      const max = Number(group.dataset.max || 3);

      if (selectedCount < min) {
        return group.dataset.errorRequired || 'Choose at least one colour.';
      }

      if (selectedCount > max) {
        return group.dataset.errorMax || 'Choose no more than three colours.';
      }

      return '';
    }

    function enforceColourLimit(control) {
      if (!control.checked) {
        return;
      }

      const groupName = control.dataset.colourInput;
      const group = getColourGroup(groupName);
      const max = Number(group?.dataset.max || 3);

      if (getSelectedColours(groupName).length <= max) {
        return;
      }

      control.checked = false;
      const message = group?.dataset.errorMax || 'Choose no more than three colours.';
      setGroupError(group?.dataset.colourField, message);
      showSummary(message);
    }

    function setControlError(control, message) {
      control.setAttribute('aria-invalid', 'true');
      setErrorText(control.dataset.field, message);
    }

    function setGroupError(fieldName, message) {
      let group;

      if (fieldName === 'package') {
        group = getPackageGroup();
      } else if (fieldName === 'designStyle') {
        group = getDesignStyleGroup();
      } else {
        group = root.querySelector(`[data-colour-field="${fieldName}"]`);
      }

      const focusTarget = group?.querySelector('input, button, select, textarea') || group;
      group?.setAttribute('aria-invalid', 'true');
      setErrorText(fieldName, message);
      return focusTarget;
    }

    function setErrorText(fieldName, message) {
      if (!fieldName) {
        return;
      }

      root.querySelectorAll(`[data-error-for="${fieldName}"]`).forEach((error) => {
        error.textContent = message;
      });
    }

    function clearStepErrors(step) {
      step.querySelectorAll('[aria-invalid="true"]').forEach((field) => field.removeAttribute('aria-invalid'));
      step.querySelectorAll('[data-error-for]').forEach((error) => {
        error.textContent = '';
      });
    }

    function clearAllErrors() {
      root.querySelectorAll('[aria-invalid="true"]').forEach((field) => field.removeAttribute('aria-invalid'));
      root.querySelectorAll('[data-error-for]').forEach((error) => {
        error.textContent = '';
      });
      hideSummary();
    }

    function showSummary(message) {
      if (!errorSummary) {
        return;
      }

      errorSummary.textContent = message;
      errorSummary.hidden = false;
    }

    function hideSummary() {
      if (!errorSummary) {
        return;
      }

      errorSummary.textContent = '';
      errorSummary.hidden = true;
    }

    function startSubmitting() {
      isSubmitting = true;
      pageIsUnloading = false;
      clearSubmitWatchdog();
      form.setAttribute('aria-busy', 'true');

      if (submitButton) {
        submitButton.setAttribute('aria-disabled', 'true');
        submitButton.textContent = 'SENDING...';
      }

      submitWatchdog = window.setTimeout(() => {
        if (pageIsUnloading) {
          return;
        }

        restoreSubmitState(
          'Your enquiry did not finish sending. Please check your connection and try again.'
        );
      }, 15000);
    }

    function restoreSubmitState(message) {
      clearSubmitWatchdog();
      isSubmitting = false;
      form.removeAttribute('aria-busy');

      if (submitButton) {
        submitButton.removeAttribute('aria-disabled');
        submitButton.textContent = submitButtonText;
      }

      if (message) {
        showSummary(message);
        errorSummary?.focus({ preventScroll: false });
      }
    }

    function clearSubmitWatchdog() {
      if (!submitWatchdog) {
        return;
      }

      window.clearTimeout(submitWatchdog);
      submitWatchdog = null;
    }

    function markPageUnloading() {
      pageIsUnloading = true;
      clearSubmitWatchdog();
    }

    function updateDerivedFields() {
      updateToggles();
      updateColourSummaries();
      updateShadePreviews();
      syncContactFields();
      updateReview();
    }

    function updateToggles() {
      root.querySelectorAll('[data-toggle-panel]').forEach((control) => {
        const target = root.querySelector(`[data-toggle-target="${control.dataset.togglePanel}"]`);
        if (target) {
          target.hidden = !control.checked;
          target.querySelectorAll('input, select, textarea').forEach((field) => {
            field.disabled = !control.checked;
          });
        }
      });

      const awaySame = getFieldControl('awaySame')?.checked;
      getColourGroup('away')?.querySelectorAll('input').forEach((input) => {
        input.disabled = Boolean(awaySame);
      });
    }

    function updateColourSummaries() {
      ['home', 'away'].forEach((groupName) => {
        const summary = root.querySelector(`[data-colour-summary="${groupName}"]`);
        if (!summary) {
          return;
        }

        let colours = getSelectedColours(groupName);

        if (groupName === 'away' && getFieldControl('awaySame')?.checked) {
          colours = getSelectedColours('home');
          summary.textContent = colours.length
            ? `Using the home kit colours: ${colours.join(', ')}.`
            : 'Using the home kit colours.';
          return;
        }

        summary.textContent = colours.length
          ? `Selected ${groupName} colours: ${colours.join(', ')}.`
          : `No ${groupName} colours selected yet.`;
      });
    }

    function updateShadePreviews() {
      root.querySelectorAll('[data-shade-input]').forEach((input) => {
        const preview = root.querySelector(`[data-shade-preview="${input.dataset.shadeInput}"]`);
        const value = input.value.trim();
        const validHex = /^#[0-9a-f]{6}$/i.test(value);

        if (!preview) {
          return;
        }

        preview.hidden = !validHex;

        if (validHex) {
          const normalized = value.toUpperCase();
          preview.querySelector('[data-shade-swatch]').style.backgroundColor = normalized;
          preview.querySelector('[data-shade-label]').textContent = `HEX preview: ${normalized}`;
        }
      });
    }

    function syncContactFields() {
      const data = getData();

      root.querySelectorAll('[data-contact-field]').forEach((field) => {
        field.value = data[field.dataset.contactField] || '';
      });

      const body = root.querySelector('[data-contact-body]');
      if (body) {
        body.value = buildBody(data);
      }
    }

    function updateReview() {
      const data = getData();

      root.querySelectorAll('[data-review-field]').forEach((target) => {
        target.textContent = data[target.dataset.reviewField] || target.dataset.empty || 'Not provided';
      });
    }

    function getData() {
      const data = {
        homeColors: getSelectedColours('home').join(', '),
        awayColors: getSelectedColours('away').join(', ')
      };

      root.querySelectorAll('[data-field]').forEach((control) => {
        const key = control.dataset.field;

        if (!key || control.disabled) {
          return;
        }

        if (control.type === 'radio') {
          if (control.checked) {
            data[key] = control.value;
          }
          return;
        }

        if (control.type === 'checkbox') {
          data[key] = control.checked ? control.value || 'Yes' : '';
          return;
        }

        data[key] = control.value.trim();
      });

      if (data.awaySame) {
        data.awayColors = data.homeColors ? `Same as home kit: ${data.homeColors}` : 'Same as home kit';
      }

      const secondSponsorParts = [];
      if (data.secondSponsor) secondSponsorParts.push('Yes');
      if (data.secondSponsorName) secondSponsorParts.push(data.secondSponsorName);
      if (data.secondSponsorPosition) secondSponsorParts.push(data.secondSponsorPosition);
      if (data.secondSponsorArtworkLink) secondSponsorParts.push(data.secondSponsorArtworkLink);
      data.secondSponsorSummary = secondSponsorParts.join(' | ');

      return data;
    }

    function buildBody(data) {
      const groups = Array.from(root.querySelectorAll('[data-review-group]'));
      const lines = ['Guided CLWB club design enquiry'];

      groups.forEach((group) => {
        const heading = group.querySelector('h3')?.textContent?.trim();
        if (heading) {
          lines.push('', heading);
        }

        group.querySelectorAll('[data-review-field]').forEach((field) => {
          const label = field.closest('div')?.querySelector('dt')?.textContent?.trim();
          const value = data[field.dataset.reviewField] || field.dataset.empty || 'Not provided';

          if (label) {
            lines.push(`${label}: ${value}`);
          }
        });
      });

      return lines.join('\n');
    }

    function saveState() {
      if (!storageAvailable) {
        return;
      }

      try {
        const state = {
          started,
          step: currentStep,
          fields: {},
          colours: {
            home: getSelectedColours('home'),
            away: getSelectedColours('away')
          }
        };

        root.querySelectorAll('[data-field]').forEach((control) => {
          const key = control.dataset.field;

          if (!key) {
            return;
          }

          if (control.type === 'radio') {
            if (control.checked) {
              state.fields[key] = control.value;
            }
            return;
          }

          state.fields[key] = control.type === 'checkbox' ? control.checked : control.value;
        });

        window.sessionStorage.setItem(storageKey, JSON.stringify(state));
      } catch (error) {
        storageAvailable = false;
      }
    }

    function restoreState() {
      if (!storageAvailable) {
        return;
      }

      try {
        const raw = window.sessionStorage.getItem(storageKey);
        if (!raw) {
          return;
        }

        const state = JSON.parse(raw);

        Object.entries(state.fields || {}).forEach(([key, value]) => {
          root.querySelectorAll('[data-field]').forEach((control) => {
            if (control.dataset.field !== key) {
              return;
            }

            if (control.type === 'radio') {
              control.checked = control.value === value;
            } else if (control.type === 'checkbox') {
              control.checked = Boolean(value);
            } else {
              control.value = value;
            }
          });
        });

        restoreColours('home', state.colours?.home || []);
        restoreColours('away', state.colours?.away || []);

        const restoredStep = Number(state.step || 1);
        currentStep = restoredStep >= 1 && restoredStep <= totalSteps ? restoredStep : 1;

        if (typeof state.started === 'boolean') {
          started = state.started;
        } else {
          started = currentStep > 1 || Object.keys(state.fields || {}).length > 0;
        }
      } catch (error) {
        clearStorage();
      }
    }

    function clearStorage() {
      if (!storageAvailable) {
        return;
      }

      try {
        window.sessionStorage.removeItem(storageKey);
      } catch (error) {
        storageAvailable = false;
      }
    }

    function hasEnteredData() {
      const data = getData();
      const ignoredDefaults = new Set(['country']);

      return Object.entries(data).some(([key, value]) => {
        if (ignoredDefaults.has(key) && value === 'United Kingdom') {
          return false;
        }

        return Boolean(String(value || '').trim());
      });
    }

    function getStep(stepNumber) {
      return steps.find((step) => Number(step.dataset.step) === stepNumber);
    }

    function getFieldControl(fieldName) {
      return Array.from(root.querySelectorAll('[data-field]')).find(
        (control) => control.dataset.field === fieldName
      );
    }

    function getColourGroup(groupName) {
      return root.querySelector(`[data-colour-group="${groupName}"]`);
    }

    function getPackageGroup() {
      return root.querySelector('[data-package-group]');
    }

    function getDesignStyleGroup() {
      return root.querySelector('[data-design-style-group]');
    }

    function getSelectedColours(groupName) {
      return Array.from(root.querySelectorAll(`[data-colour-input="${groupName}"]:checked`)).map(
        (input) => input.value
      );
    }

    function restoreColours(groupName, colours) {
      root.querySelectorAll(`[data-colour-input="${groupName}"]`).forEach((input) => {
        input.checked = colours.includes(input.value);
      });
    }

    function prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initJourneys(), { once: true });
  } else {
    initJourneys();
  }

  if (document.documentElement.dataset[listenerKey] !== 'true') {
    document.documentElement.dataset[listenerKey] = 'true';
    document.addEventListener('shopify:section:load', (event) => initJourneys(event.target));
  }
})();
